import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Users, Bed, Maximize2, Bath, Check, ChevronRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/Seo";
import { api, fmtMoney, todayISO, addDaysISO } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";

export default function RoomDetailPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t, trField } = useI18n();
  const { format } = useCurrency();
  const [room, setRoom] = useState(null);
  const [active, setActive] = useState(0);
  const [checkIn, setCheckIn] = useState(params.get("check_in") || todayISO());
  const [checkOut, setCheckOut] = useState(params.get("check_out") || addDaysISO(todayISO(), 2));
  const [guests, setGuests] = useState(Number(params.get("guests") || 2));
  const [avail, setAvail] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [showCal, setShowCal] = useState(false);

  useEffect(() => {
    api.get(`/rooms/${id}`).then((r) => setRoom(r.data)).catch(() => nav("/rooms"));
    api.get(`/rooms/${id}/blocked-dates`).then((r) => setBlocked(r.data.blocked || [])).catch(() => {});
  }, [id, nav]);

  async function checkAvail() {
    const r = await api.get(`/rooms/${id}/availability`, { params: { check_in: checkIn, check_out: checkOut } });
    setAvail(r.data.available);
  }

  function goBook() {
    const q = new URLSearchParams({ check_in: checkIn, check_out: checkOut, guests: String(guests) });
    nav(`/book/${id}?${q.toString()}`);
  }

  // Expand blocked ranges into a Set of YYYY-MM-DD
  const blockedSet = useMemo(() => {
    const s = new Set();
    for (const b of blocked) {
      let d = new Date(b.check_in);
      const end = new Date(b.check_out);
      while (d < end) {
        s.add(d.toISOString().slice(0, 10));
        d.setDate(d.getDate() + 1);
      }
    }
    return s;
  }, [blocked]);

  if (!room) return <SiteLayout><div className="py-24 text-center">Loading…</div></SiteLayout>;

  const name = trField(room, "name");
  const short = trField(room, "short_description");
  const desc = trField(room, "description");

  const nights = Math.max(
    Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)), 0
  );
  const subtotal = nights * room.price_per_night;
  const taxes = subtotal * 0.1;
  const total = subtotal + taxes;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HotelRoom",
    name,
    description: desc,
    image: room.photos,
    occupancy: { "@type": "QuantitativeValue", maxValue: room.max_guests },
    offers: { "@type": "Offer", price: room.price_per_night, priceCurrency: "EUR" },
  };

  return (
    <SiteLayout>
      <Seo title={`${name} · Berdhill Rooms`} description={short} image={room.photos[0]} jsonLd={jsonLd} type="product" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-8">
        <button onClick={() => nav(-1)} className="text-sm text-brand-muted hover:text-brand-text">{t("detail.back")}</button>
      </div>

      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mt-6">
        <div className="grid lg:grid-cols-4 gap-3">
          <div className="lg:col-span-3 aspect-[16/10] overflow-hidden rounded-sm">
            <img src={room.photos[active] || room.photos[0]} alt={name} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
            {room.photos.slice(0, 4).map((p, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                data-testid={`room-thumb-${i}`}
                className={`aspect-square overflow-hidden rounded-sm border-2 transition ${i === active ? "border-brand-primary" : "border-transparent"}`}
              >
                <img src={p} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mt-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("detail.room")}</div>
          <h1 data-testid="room-detail-name" className="font-display text-4xl md:text-5xl mt-3">{name}</h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-brand-muted">
            <span className="flex items-center gap-2"><Users size={15} />{room.max_guests} {room.max_guests > 1 ? t("widget.guests_plural") : t("widget.guest")}</span>
            <span className="flex items-center gap-2"><Bed size={15} />{room.beds} bed{room.beds > 1 ? "s" : ""}</span>
            <span className="flex items-center gap-2"><Bath size={15} />{room.bathrooms} bath{room.bathrooms > 1 ? "s" : ""}</span>
            {room.size_sqm ? <span className="flex items-center gap-2"><Maximize2 size={15} />{room.size_sqm} m²</span> : null}
          </div>
          <p className="mt-8 text-brand-text/80 leading-relaxed text-base md:text-lg whitespace-pre-line">{desc}</p>

          <div className="mt-12">
            <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("detail.amenities")}</div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {(room.amenities || []).map((a) => (
                <div key={a} className="flex items-center gap-3 text-sm">
                  <Check size={16} className="text-brand-primary" /> {a}
                </div>
              ))}
            </div>
          </div>

          {/* Availability mini-calendar */}
          <div className="mt-12">
            <div className="flex justify-between items-center">
              <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("detail.calendar")}</div>
              <button onClick={() => setShowCal(!showCal)} className="text-xs text-brand-primary">
                {showCal ? "Hide" : "Show"}
              </button>
            </div>
            {showCal && <MiniCalendar blockedSet={blockedSet} />}
            <div className="mt-3 flex gap-4 text-xs text-brand-muted">
              <span className="flex items-center gap-2"><i className="inline-block w-3 h-3 rounded-sm bg-brand-border" /> {t("detail.free")}</span>
              <span className="flex items-center gap-2"><i className="inline-block w-3 h-3 rounded-sm bg-brand-error/70" /> {t("detail.blocked")}</span>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 bg-white border border-brand-border rounded-sm p-6 md:p-8">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-3xl text-brand-primary">{format(room.price_per_night)}</span>
              <span className="text-brand-muted text-sm">{t("rooms.per_night")}</span>
            </div>
            <hr className="my-6 border-brand-border" />
            <div className="grid grid-cols-2 gap-3">
              <DateField label={t("widget.checkin")} value={checkIn} min={todayISO()} onChange={(v) => {
                setCheckIn(v); if (v >= checkOut) setCheckOut(addDaysISO(v, 1));
              }} testId="detail-checkin" />
              <DateField label={t("widget.checkout")} value={checkOut} min={addDaysISO(checkIn, 1)} onChange={setCheckOut} testId="detail-checkout" />
            </div>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">{t("widget.guests")}</label>
              <select
                data-testid="detail-guests"
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                className="mt-1 w-full border border-brand-border rounded-sm px-3 py-2 text-sm bg-white"
              >
                {Array.from({ length: room.max_guests }).map((_, i) => (
                  <option key={i} value={i + 1}>{i + 1} {i + 1 > 1 ? t("widget.guests_plural") : t("widget.guest")}</option>
                ))}
              </select>
            </div>

            {nights > 0 && (
              <div className="mt-6 text-sm space-y-2">
                <Row label={`${format(room.price_per_night)} × ${nights} nights`} value={format(subtotal)} />
                <Row label="Taxes & fees" value={format(taxes)} />
                <hr className="my-3 border-brand-border" />
                <Row label="Total" value={<span className="font-medium text-brand-primary">{format(total)}</span>} />
                <div className="text-[11px] text-brand-muted">Charged in EUR · {fmtMoney(total)} at checkout</div>
              </div>
            )}

            <button
              data-testid="check-availability-btn"
              onClick={checkAvail}
              className="w-full mt-6 border border-brand-primary text-brand-primary hover:bg-brand-secondary py-3 rounded-sm text-sm font-medium transition-colors"
            >
              {t("detail.check_avail")}
            </button>
            {avail === true && <div className="mt-2 text-xs text-brand-success">{t("detail.available")}</div>}
            {avail === false && <div className="mt-2 text-xs text-brand-error">{t("detail.not_avail")}</div>}

            <button
              data-testid="reserve-now-btn"
              onClick={goBook}
              className="w-full mt-3 bg-brand-primary hover:bg-brand-primaryHover text-white py-3.5 rounded-sm text-sm font-medium tracking-wide flex items-center justify-center gap-2 transition-colors"
            >
              {t("detail.reserve")} <ChevronRight size={16} />
            </button>
            <p className="mt-3 text-[11px] text-brand-muted text-center">{t("detail.free_cancel")}</p>
          </div>
        </aside>
      </section>

      <div className="h-24" />
    </SiteLayout>
  );
}

function DateField({ label, value, onChange, min, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">{label}</span>
      <input
        data-testid={testId}
        type="date"
        value={value}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-brand-border rounded-sm px-3 py-2 text-sm bg-white"
      />
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-brand-muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function MiniCalendar({ blockedSet }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const months = [0, 1];
  return (
    <div className="mt-4 grid md:grid-cols-2 gap-6">
      {months.map((offset) => (
        <MonthGrid key={offset} date={addMonths(cursor, offset)} blockedSet={blockedSet}
          onPrev={offset === 0 ? () => setCursor(addMonths(cursor, -1)) : null}
          onNext={offset === 1 ? () => setCursor(addMonths(cursor, 1)) : null}
        />
      ))}
    </div>
  );
}

function addMonths(d, n) {
  const nd = new Date(d); nd.setMonth(nd.getMonth() + n); return nd;
}

function MonthGrid({ date, blockedSet, onPrev, onNext }) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startWeekday = (first.getDay() + 6) % 7; // Mon=0
  const days = [];
  for (let i = 0; i < startWeekday; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(d);
  const monthName = first.toLocaleString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="bg-white border border-brand-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onPrev} disabled={!onPrev} className="text-xs text-brand-muted disabled:opacity-30 px-2">‹</button>
        <div className="text-sm font-medium">{monthName}</div>
        <button onClick={onNext} disabled={!onNext} className="text-xs text-brand-muted disabled:opacity-30 px-2">›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <div key={i} className="text-[10px] uppercase tracking-wider text-brand-muted">{d}</div>
        ))}
        {days.map((d, i) => {
          if (d === null) return <div key={i} />;
          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const isBlocked = blockedSet.has(iso);
          const isPast = new Date(iso) < new Date(new Date().toDateString());
          return (
            <div
              key={i}
              data-testid={`cal-${iso}`}
              title={isBlocked ? "Booked" : "Available"}
              className={`aspect-square flex items-center justify-center text-xs rounded-sm ${
                isPast ? "text-brand-border" : isBlocked ? "bg-brand-error/70 text-white line-through" : "bg-brand-bg text-brand-text hover:bg-brand-secondary"
              }`}
            >
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}
